import { useRef, useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, Alert } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { UploadFileOutlined as UploadIcon } from '@mui/icons-material';
import { FileFieldActions } from './FileFieldActions';
import { DetailFormFieldShell } from './DetailFormFieldShell';
import {
  useUploadFieldFileMutation,
  useLazyGetDirectoryQuery,
  useDeleteFileMutation,
} from '../file-storage/api/fileStorageApi';
import { extractErrorMessage } from '../../utils/errorUtils';
import { usePreSave } from './PreSaveContext';
import type { ColumnMetadata } from '../grid';
import type { DetailFieldApi } from './types';

const DEFAULT_UPLOAD_FOLDER = 'uploads';

const getAcceptAttribute = (metadata: ColumnMetadata): string | undefined => {
  const { allowedTypes, extension } = metadata as Record<string, unknown>;
  if (Array.isArray(allowedTypes)) {
    return allowedTypes.filter((t): t is string => typeof t === 'string').join(',');
  }
  return typeof extension === 'string' ? extension : undefined;
};

const getMaxFileSize = (metadata: ColumnMetadata): number | undefined => {
  const { maxFileSize } = metadata as Record<string, unknown>;
  return typeof maxFileSize === 'number' ? maxFileSize : undefined;
};

const getExtensionLabel = (metadata: ColumnMetadata): string => {
  const { extension } = metadata as Record<string, unknown>;
  return typeof extension === 'string' ? extension : '';
};

type DetailFormFileFieldProps = {
  fieldName: string;
  metadata: ColumnMetadata;
  field: DetailFieldApi;
  readOnly?: boolean;
  isRequired: boolean;
  hideCaption?: boolean;
};

export const DetailFormFileField = ({
  fieldName,
  metadata,
  field,
  readOnly = false,
  isRequired,
  hideCaption,
}: DetailFormFileFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [getDirectory] = useLazyGetDirectoryQuery();
  const [uploadFieldFile, { isLoading: isUploading }] = useUploadFieldFileMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileGuid = typeof field.state.value === 'string' ? field.state.value : '';
  const label = `${metadata.title || metadata.caption || fieldName}${isRequired ? ' *' : ''}`;

  const rawFolder = (metadata as Record<string, unknown>).folder;
  const uploadFolder =
    typeof rawFolder === 'string' && rawFolder.trim() !== ''
      ? rawFolder.trim()
      : DEFAULT_UPLOAD_FOLDER;

  const preSave = usePreSave();
  const pendingDeleteRef = useRef<string | null>(null);

  useEffect(() => {
    if (!preSave) return;
    return preSave.register(async () => {
      if (!pendingDeleteRef.current) return;
      const guidToDelete = pendingDeleteRef.current;
      pendingDeleteRef.current = null;
      try {
        await deleteFile({ fileId: guidToDelete }).unwrap();
      } catch {
        console.log(`Failed to delete file ${guidToDelete}`);
      }
    });
  }, [preSave, deleteFile]);

  const handlePick = () => inputRef.current?.click();

  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadError(null);
    try {
      const destinationPathInfo = await getDirectory({ dirName: uploadFolder }).unwrap();
      const newGuid = await uploadFieldFile({ file, destinationPathInfo }).unwrap();
      pendingDeleteRef.current = null;
      field.handleChange(newGuid);
    } catch (error) {
      setUploadError(extractErrorMessage(error, 'Failed to upload file'));
    }
  };

  const handleDelete = () => {
    if (!fileGuid) return;
    setUploadError(null);
    pendingDeleteRef.current = fileGuid;
    field.handleChange('');
  };

  const canEdit = metadata.allowEditing ?? true;
  const maxFileSize = getMaxFileSize(metadata);

  return (
    <DetailFormFieldShell
      label={label}
      hideCaption
      error={field.state.meta.errors[0] as string | undefined}
    >
      <Box
        sx={{
          position: 'relative',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          pt: 2,
          pb: 1.5,
          px: 1.5,
        }}
      >
        {!hideCaption && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: -8,
              left: 8,
              bgcolor: 'background.paper',
              px: 0.5,
              color: 'text.secondary',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {readOnly && <LockOutlined sx={{ fontSize: 14 }} aria-hidden="true" />}
              {label}
            </span>
          </Typography>
        )}
        {fileGuid ? (
          <FileFieldActions
            fileGuid={fileGuid}
            fallbackName={metadata.caption || fieldName}
            disabled={isDeleting}
            onDelete={!readOnly && !isRequired && canEdit ? handleDelete : undefined}
          />
        ) : (
          <Box>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={getAcceptAttribute(metadata)}
              onChange={handleFileSelected}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isUploading ? <CircularProgress size={14} /> : <UploadIcon fontSize="small" />
              }
              onClick={handlePick}
              disabled={readOnly || isUploading || !canEdit}
            >
              upload
            </Button>
            {maxFileSize != null && maxFileSize > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                max {maxFileSize}MB, {getExtensionLabel(metadata)}
              </Typography>
            )}
            {uploadError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {uploadError}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </DetailFormFieldShell>
  );
};
